import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Ban, CheckCircle, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Device {
  id: string;
  user_id: string;
  device_fingerprint: string;
  user_agent: string | null;
  ip_address: string | null;
  last_login: string;
  is_blocked: boolean;
  blocked_at: string | null;
  blocked_reason: string | null;
  created_at: string;
  user_email?: string;
  is_admin?: boolean;
}

interface Profile {
  user_id: string;
  email: string;
}

const AdminDevices = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockReason, setBlockReason] = useState("");

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      // Fetch devices
      const { data: devicesData, error: devicesError } = await supabase
        .from("user_devices")
        .select("*")
        .order("last_login", { ascending: false });

      if (devicesError) {
        console.error("Error fetching devices:", devicesError);
        return;
      }

      // Fetch profiles to get emails
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, email");

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      }

      // Fetch admin emails to check if user is admin
      const { data: adminEmails, error: adminError } = await supabase
        .from("admin_emails")
        .select("email");

      if (adminError) {
        console.error("Error fetching admin emails:", adminError);
      }

      const adminEmailSet = new Set((adminEmails || []).map((a) => a.email));

      // Map emails to devices
      const profilesMap = new Map<string, string>();
      (profilesData || []).forEach((profile: Profile) => {
        profilesMap.set(profile.user_id, profile.email);
      });

      const devicesWithEmail = (devicesData || []).map((device) => {
        const email = profilesMap.get(device.user_id) || "غير معروف";
        return {
          ...device,
          user_email: email,
          is_admin: adminEmailSet.has(email),
        };
      });

      setDevices(devicesWithEmail);
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDevice = async (deviceId: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      const { error } = await supabase
        .from("user_devices")
        .update({
          is_blocked: true,
          blocked_at: new Date().toISOString(),
          blocked_reason: blockReason || "محظور بواسطة المسؤول",
        })
        .eq("id", deviceId);

      if (error) {
        toast.error("فشل في حظر الجهاز");
        console.error(error);
      } else {
        // Ban the user's email/account
        if (device?.user_id) {
          try {
            await supabase.functions.invoke("ban-user", {
              body: { action: "ban", userId: device.user_id },
            });
            toast.success("تم حظر الجهاز والحساب بنجاح");
          } catch {
            toast.success("تم حظر الجهاز (فشل حظر الحساب)");
          }
        } else {
          toast.success("تم حظر الجهاز بنجاح");
        }
        setBlockReason("");
        fetchDevices();
      }
    } catch (error) {
      toast.error("خطأ في حظر الجهاز");
    }
  };

  const handleUnblockDevice = async (deviceId: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      const { error } = await supabase
        .from("user_devices")
        .update({
          is_blocked: false,
          blocked_at: null,
          blocked_reason: null,
        })
        .eq("id", deviceId);

      if (error) {
        toast.error("فشل في إلغاء حظر الجهاز");
        console.error(error);
      } else {
        // Unban the user's account
        if (device?.user_id) {
          try {
            await supabase.functions.invoke("ban-user", {
              body: { action: "unban", userId: device.user_id },
            });
            toast.success("تم إلغاء حظر الجهاز والحساب بنجاح");
          } catch {
            toast.success("تم إلغاء حظر الجهاز (فشل إلغاء حظر الحساب)");
          }
        } else {
          toast.success("تم إلغاء حظر الجهاز بنجاح");
        }
        fetchDevices();
      }
    } catch (error) {
      toast.error("خطأ في إلغاء حظر الجهاز");
    }
  };

  const handleBlockByIP = async (ipAddress: string) => {
    if (!ipAddress) {
      toast.error("عنوان IP غير متوفر");
      return;
    }

    // حماية: لا تحظر IP إذا كان عليه جهاز مسؤول
    const hasAdminOnIP = devices.some(
      (d) => d.ip_address === ipAddress && d.is_admin
    );
    if (hasAdminOnIP) {
      toast.error("لا يمكن حظر IP يحتوي على جهاز مسؤول");
      return;
    }

    try {
      const { error } = await supabase
        .from("user_devices")
        .update({
          is_blocked: true,
          blocked_at: new Date().toISOString(),
          blocked_reason: blockReason || `محظور بناءً على IP: ${ipAddress}`,
        })
        .eq("ip_address", ipAddress);

      if (error) {
        toast.error("فشل في حظر عنوان IP");
        console.error(error);
      } else {
        toast.success(`تم حظر جميع الأجهزة بعنوان IP: ${ipAddress}`);
        setBlockReason("");
        fetchDevices();
      }
    } catch (error) {
      toast.error("خطأ في حظر عنوان IP");
    }
  };

  const handleUnblockByIP = async (ipAddress: string) => {
    if (!ipAddress) {
      toast.error("عنوان IP غير متوفر");
      return;
    }

    try {
      const { error } = await supabase
        .from("user_devices")
        .update({
          is_blocked: false,
          blocked_at: null,
          blocked_reason: null,
        })
        .eq("ip_address", ipAddress);

      if (error) {
        toast.error("فشل في إلغاء حظر عنوان IP");
        console.error(error);
      } else {
        toast.success(`تم إلغاء حظر جميع الأجهزة بعنوان IP: ${ipAddress}`);
        fetchDevices();
      }
    } catch (error) {
      toast.error("خطأ في إلغاء حظر عنوان IP");
    }
  };

  const getBlockedIPsCount = (ipAddress: string | null) => {
    if (!ipAddress) return 0;
    return devices.filter(d => d.ip_address === ipAddress && d.is_blocked).length;
  };

  const getTotalDevicesForIP = (ipAddress: string | null) => {
    if (!ipAddress) return 0;
    return devices.filter(d => d.ip_address === ipAddress).length;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseUserAgent = (ua: string | null) => {
    if (!ua) return "غير معروف";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return "متصفح آخر";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const activeDevices = devices.filter((d) => !d.is_blocked);
  const blockedDevices = devices.filter((d) => d.is_blocked);

  const renderDeviceRow = (device: Device) => (
    <TableRow key={device.id} className="border-border">
      <TableCell className="text-foreground font-medium">
        <div className="flex items-center gap-2">
          {device.user_email}
          {device.is_admin && (
            <Badge className="bg-primary/20 text-primary text-xs">مسؤول</Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-foreground">
        {formatDate(device.last_login)}
      </TableCell>
      <TableCell className="text-foreground">
        {parseUserAgent(device.user_agent)}
      </TableCell>
      <TableCell className="text-muted-foreground font-mono text-sm">
        <div className="flex items-center gap-2">
          <span className="bg-secondary px-2 py-1 rounded">{device.ip_address || "-"}</span>
          {device.ip_address && getTotalDevicesForIP(device.ip_address) > 1 && (
            <Badge variant="outline" className="text-xs">
              {getTotalDevicesForIP(device.ip_address)} أجهزة
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        {device.is_blocked ? (
          <Badge className="bg-destructive/20 text-destructive">محظور</Badge>
        ) : (
          <Badge className="bg-crypto-green/20 text-crypto-green">نشط</Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            {/* Block/Unblock Device - لا يمكن حظر المسؤولين (لكن يمكن إلغاء الحظر إذا كان محظوراً) */}
            {device.is_admin && device.is_blocked ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnblockDevice(device.id)}
                    className="text-crypto-green hover:text-crypto-green/80"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>إلغاء حظر جهاز المسؤول</TooltipContent>
              </Tooltip>
            ) : device.is_admin ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground text-xs px-2">محمي</span>
                </TooltipTrigger>
                <TooltipContent>لا يمكن حظر أجهزة المسؤولين</TooltipContent>
              </Tooltip>
            ) : device.is_blocked ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnblockDevice(device.id)}
                    className="text-crypto-green hover:text-crypto-green/80"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>إلغاء حظر الجهاز</TooltipContent>
              </Tooltip>
            ) : (
              <AlertDialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>حظر الجهاز</TooltipContent>
                </Tooltip>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>حظر هذا الجهاز؟</AlertDialogTitle>
                    <AlertDialogDescription>
                      سيتم منع هذا الجهاز من الوصول للمنصة.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    placeholder="سبب الحظر (اختياري)"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    className="mt-2"
                  />
                  <AlertDialogFooter className="flex-row-reverse gap-2">
                    <AlertDialogCancel>إلغاء</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleBlockDevice(device.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      حظر الجهاز
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {/* Block/Unblock by IP - لا يمكن حظر IP المسؤولين */}
            {device.ip_address && !device.is_admin && (
              <>
                {getBlockedIPsCount(device.ip_address) === getTotalDevicesForIP(device.ip_address) ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnblockByIP(device.ip_address!)}
                        className="text-crypto-green hover:text-crypto-green/80"
                      >
                        <Globe className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>إلغاء حظر IP: {device.ip_address}</TooltipContent>
                  </Tooltip>
                ) : (
                  <AlertDialog>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:text-primary/80"
                          >
                            <Globe className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>حظر جميع الأجهزة بـ IP: {device.ip_address}</TooltipContent>
                    </Tooltip>
                    <AlertDialogContent dir="rtl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>حظر عنوان IP؟</AlertDialogTitle>
                        <AlertDialogDescription>
                          سيتم حظر جميع الأجهزة ({getTotalDevicesForIP(device.ip_address)}) المتصلة من عنوان IP: <br />
                          <span className="font-mono font-bold">{device.ip_address}</span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Input
                        placeholder="سبب الحظر (اختياري)"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        className="mt-2"
                      />
                      <AlertDialogFooter className="flex-row-reverse gap-2">
                        <AlertDialogCancel>إلغاء</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleBlockByIP(device.ip_address!)}
                          className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          حظر جميع الأجهزة
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            )}
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      {/* الأجهزة النشطة */}
      <Card className="glass border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-crypto-green" />
            الأجهزة النشطة ({activeDevices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeDevices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد أجهزة نشطة
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-right text-muted-foreground">المستخدم</TableHead>
                    <TableHead className="text-right text-muted-foreground">آخر تسجيل</TableHead>
                    <TableHead className="text-right text-muted-foreground">المتصفح</TableHead>
                    <TableHead className="text-right text-muted-foreground">IP</TableHead>
                    <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                    <TableHead className="text-right text-muted-foreground">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{activeDevices.map(renderDeviceRow)}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* الأجهزة المحظورة */}
      <Card className="glass border-destructive/30">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Ban className="w-5 h-5 text-destructive" />
            الأجهزة المحظورة ({blockedDevices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockedDevices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد أجهزة محظورة
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-right text-muted-foreground">المستخدم</TableHead>
                    <TableHead className="text-right text-muted-foreground">آخر تسجيل</TableHead>
                    <TableHead className="text-right text-muted-foreground">المتصفح</TableHead>
                    <TableHead className="text-right text-muted-foreground">IP</TableHead>
                    <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                    <TableHead className="text-right text-muted-foreground">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{blockedDevices.map(renderDeviceRow)}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDevices;
